<template>
      <div class="customer-service-orders">
      <div class="page-header">
        <div class="header-content">
          <div class="header-left">
            <h2>客户对接收款管理</h2>
            <p>管理需要客服对接收款的订单</p>
          </div>
          <div class="header-actions">
            <el-button type="primary" @click="goBack" icon="el-icon-back">
              返回首页
            </el-button>
          </div>
        </div>
      </div>

    <div class="content-wrapper">
      <!-- 统计卡片 -->
      <div class="stats-cards">
        <div class="stat-card">
          <div class="stat-icon">
            <i class="el-icon-wallet"></i>
          </div>
          <div class="stat-info">
            <div class="stat-number">{{ pendingOrders.length }}</div>
            <div class="stat-label">待收款订单</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon">
            <i class="el-icon-money"></i>
          </div>
          <div class="stat-info">
            <div class="stat-number">{{ totalPendingAmount }}</div>
            <div class="stat-label">待收款总额(元)</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon">
            <i class="el-icon-check"></i>
          </div>
          <div class="stat-info">
            <div class="stat-number">{{ completedOrders.length }}</div>
            <div class="stat-label">已收款订单</div>
          </div>
        </div>
      </div>

      <!-- 订单列表 -->
      <div class="orders-section">
        <div class="section-header">
          <h3>客户对接订单列表</h3>
          <el-button type="primary" @click="refreshOrders" :loading="loading">
            <i class="el-icon-refresh"></i> 刷新
          </el-button>
        </div>

        <el-table
          :data="customerServiceOrders"
          v-loading="loading"
          style="width: 100%"
          :row-class-name="getRowClassName"
        >
          <el-table-column prop="id" label="订单号" width="100">
            <template slot-scope="scope">
              <span class="order-id">#{{ scope.row.id }}</span>
            </template>
          </el-table-column>

          <el-table-column prop="customer_name" label="客户姓名" width="120">
            <template slot-scope="scope">
              <div class="customer-info">
                <div class="customer-name">{{ scope.row.customer_name }}</div>
                <div class="customer-phone">{{ scope.row.contact_phone }}</div>
              </div>
            </template>
          </el-table-column>

          <el-table-column prop="appliance_type" label="设备类型" width="120">
            <template slot-scope="scope">
              <el-tag :type="getApplianceTypeColor(scope.row.appliance_type)">
                {{ getApplianceTypeName(scope.row.appliance_type) }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column prop="problem_description" label="问题描述" min-width="200">
            <template slot-scope="scope">
              <div class="problem-desc">{{ scope.row.problem_description }}</div>
            </template>
          </el-table-column>

          <el-table-column prop="service_fee" label="维修费用" width="120">
            <template slot-scope="scope">
              <span class="service-fee">¥{{ scope.row.service_fee }}</span>
            </template>
          </el-table-column>

          <el-table-column prop="actual_payment" label="实际收款" width="150">
            <template slot-scope="scope">
              <el-input-number
                v-if="scope.row.payment_status === 'pending'"
                v-model="scope.row.actual_payment"
                :min="0"
                :max="9999"
                :precision="2"
                size="small"
                style="width: 100px"
                placeholder="收款金额"
              ></el-input-number>
              <span v-else class="actual-payment">¥{{ scope.row.actual_payment || scope.row.service_fee }}</span>
            </template>
          </el-table-column>

          <el-table-column prop="payment_status" label="收款状态" width="100">
            <template slot-scope="scope">
              <el-tag :type="scope.row.payment_status === 'completed' ? 'success' : 'warning'">
                {{ scope.row.payment_status === 'completed' ? '已收款' : '待收款' }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column prop="worker_name" label="维修师傅" width="120">
            <template slot-scope="scope">
              <span>{{ scope.row.worker_name }}</span>
            </template>
          </el-table-column>

          <el-table-column prop="completed_at" label="完成时间" width="160">
            <template slot-scope="scope">
              <span>{{ formatDate(scope.row.completed_at) }}</span>
            </template>
          </el-table-column>

          <el-table-column label="操作" width="150" fixed="right">
            <template slot-scope="scope">
              <el-button
                v-if="scope.row.payment_status === 'pending'"
                type="success"
                size="small"
                @click="confirmPayment(scope.row)"
                :loading="scope.row.confirming"
              >
                确认收款
              </el-button>
              <el-button
                type="primary"
                size="small"
                @click="viewOrderDetail(scope.row)"
              >
                查看详情
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-wrapper">
          <el-pagination
            @current-change="handleCurrentChange"
            @size-change="handleSizeChange"
            :current-page="currentPage"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
          >
          </el-pagination>
        </div>
      </div>
    </div>

    <!-- 订单详情对话框 -->
    <el-dialog
      title="订单详情"
      :visible.sync="detailVisible"
      width="800px"
      :before-close="handleDetailClose"
    >
      <div v-if="selectedOrder" class="order-detail">
        <div class="detail-section">
          <h4>基本信息</h4>
          <el-row :gutter="20">
            <el-col :span="12">
              <div class="detail-item">
                <label>订单号：</label>
                <span>#{{ selectedOrder.id }}</span>
              </div>
            </el-col>
            <el-col :span="12">
              <div class="detail-item">
                <label>客户姓名：</label>
                <span>{{ selectedOrder.customer_name }}</span>
              </div>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="12">
              <div class="detail-item">
                <label>联系电话：</label>
                <span>{{ selectedOrder.contact_phone }}</span>
              </div>
            </el-col>
            <el-col :span="12">
              <div class="detail-item">
                <label>设备类型：</label>
                <span>{{ getApplianceTypeName(selectedOrder.appliance_type) }}</span>
              </div>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="12">
              <div class="detail-item">
                <label>维修费用：</label>
                <span class="fee">¥{{ selectedOrder.service_fee }}</span>
              </div>
            </el-col>
            <el-col :span="12">
              <div class="detail-item">
                <label>实际收款：</label>
                <span class="fee">¥{{ selectedOrder.actual_payment || selectedOrder.service_fee }}</span>
              </div>
            </el-col>
          </el-row>
        </div>

        <div class="detail-section">
          <h4>问题描述</h4>
          <p>{{ selectedOrder.problem_description }}</p>
        </div>

        <div class="detail-section">
          <h4>维修记录</h4>
          <div v-if="repairRecords.length > 0">
            <div v-for="record in repairRecords" :key="record.id" class="repair-record">
              <div class="record-header">
                <span class="record-time">{{ formatDate(record.repair_time) }}</span>
                <el-tag :type="getResultType(record.result)">
                  {{ getResultName(record.result) }}
                </el-tag>
              </div>
              <div class="record-content">
                <p><strong>问题原因：</strong>{{ record.cause }}</p>
                <p><strong>解决方案：</strong>{{ record.solution }}</p>
                <p v-if="record.replaced_parts"><strong>更换配件：</strong>{{ record.replaced_parts }}</p>
                <p v-if="record.suggestions"><strong>使用建议：</strong>{{ record.suggestions }}</p>
              </div>
            </div>
          </div>
          <p v-else class="no-data">暂无维修记录</p>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'CustomerServiceOrders',
  data() {
    return {
      loading: false,
      customerServiceOrders: [],
      currentPage: 1,
      pageSize: 20,
      total: 0,
      detailVisible: false,
      selectedOrder: null,
      repairRecords: []
    }
  },
  computed: {
    pendingOrders() {
      return this.customerServiceOrders.filter(order => order.payment_status === 'pending')
    },
    completedOrders() {
      return this.customerServiceOrders.filter(order => order.payment_status === 'completed')
    },
    totalPendingAmount() {
      return this.pendingOrders.reduce((sum, order) => sum + parseFloat(order.service_fee || 0), 0).toFixed(2)
    }
  },
  mounted() {
    this.fetchCustomerServiceOrders()
  },
  methods: {
    async fetchCustomerServiceOrders() {
      try {
        this.loading = true
        const response = await this.$http.get('/api/admin/customer-service-orders', {
          params: {
            page: this.currentPage,
            pageSize: this.pageSize
          }
        })
        
        if (response.success) {
          this.customerServiceOrders = response.data.orders
          this.total = response.data.total
        } else {
          this.$message.error(response.message || '获取订单列表失败')
        }
      } catch (error) {
        console.error('获取客户对接订单失败:', error)
        this.$message.error('获取订单列表失败')
      } finally {
        this.loading = false
      }
    },

    async confirmPayment(order) {
      if (!order.actual_payment || order.actual_payment <= 0) {
        this.$message.warning('请先设置实际收款金额')
        return
      }

      try {
        this.$set(order, 'confirming', true)
        
        const response = await this.$http.put('/api/admin/confirm-payment', {
          orderId: order.id,
          actualPayment: order.actual_payment
        })
        
        if (response.success) {
          this.$message.success('收款确认成功')
          order.payment_status = 'completed'
          order.status = 'completed'
          this.refreshOrders()
        } else {
          this.$message.error(response.message || '收款确认失败')
        }
      } catch (error) {
        console.error('确认收款失败:', error)
        this.$message.error('收款确认失败')
      } finally {
        this.$set(order, 'confirming', false)
      }
    },

    async viewOrderDetail(order) {
      this.selectedOrder = order
      this.detailVisible = true
      
      try {
        const response = await this.$http.get(`/api/admin/repair-records/${order.id}`)
        if (response.success) {
          this.repairRecords = response.data.records
        }
      } catch (error) {
        console.error('获取维修记录失败:', error)
        this.repairRecords = []
      }
    },

    handleDetailClose() {
      this.detailVisible = false
      this.selectedOrder = null
      this.repairRecords = []
    },

    refreshOrders() {
      this.fetchCustomerServiceOrders()
    },

    handleCurrentChange(page) {
      this.currentPage = page
      this.fetchCustomerServiceOrders()
    },

    handleSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.fetchCustomerServiceOrders()
    },

    getRowClassName({ row }) {
      return row.payment_status === 'pending' ? 'pending-row' : 'completed-row'
    },

    getApplianceTypeName(type) {
      const typeMap = {
        'air_conditioner': '空调',
        'refrigerator': '冰箱',
        'washing_machine': '洗衣机',
        'television': '电视',
        'water_heater': '热水器',
        'microwave': '微波炉'
      }
      return typeMap[type] || type
    },

    getApplianceTypeColor(type) {
      const colorMap = {
        'air_conditioner': 'primary',
        'refrigerator': 'success',
        'washing_machine': 'warning',
        'television': 'info',
        'water_heater': 'danger',
        'microwave': ''
      }
      return colorMap[type] || ''
    },

    getResultType(result) {
      const typeMap = {
        'success': 'success',
        'partial': 'warning',
        'failed': 'danger'
      }
      return typeMap[result] || 'info'
    },

    getResultName(result) {
      const nameMap = {
        'success': '维修成功',
        'partial': '部分修复',
        'failed': '无法修复'
      }
      return nameMap[result] || result
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleString('zh-CN')
    },

    goBack() {
      this.$router.push('/admin/home')
    }
  }
}
</script>

<style scoped>
.customer-service-orders {
  padding: 20px;
}

.page-header {
  margin-bottom: 30px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.header-left h2 {
  margin: 0 0 10px 0;
  color: #303133;
  font-size: 2rem;
}

.header-left p {
  margin: 0;
  color: #606266;
  font-size: 1rem;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.content-wrapper {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  padding: 20px;
  background: #f5f7fa;
}

.stat-card {
  display: flex;
  align-items: center;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  font-size: 1.5rem;
  color: white;
}

.stat-card:nth-child(1) .stat-icon {
  background: linear-gradient(45deg, #409EFF, #66b3ff);
}

.stat-card:nth-child(2) .stat-icon {
  background: linear-gradient(45deg, #67C23A, #85ce61);
}

.stat-card:nth-child(3) .stat-icon {
  background: linear-gradient(45deg, #E6A23C, #ebb563);
}

.stat-info {
  flex: 1;
}

.stat-number {
  font-size: 2rem;
  font-weight: bold;
  color: #303133;
  line-height: 1;
}

.stat-label {
  margin-top: 5px;
  color: #606266;
  font-size: 0.9rem;
}

.orders-section {
  padding: 20px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-header h3 {
  margin: 0;
  color: #303133;
  font-size: 1.3rem;
}

.order-id {
  font-weight: bold;
  color: #409EFF;
}

.customer-info {
  line-height: 1.4;
}

.customer-name {
  font-weight: 500;
  color: #303133;
}

.customer-phone {
  font-size: 0.85rem;
  color: #606266;
}

.problem-desc {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.service-fee {
  font-weight: bold;
  color: #E6A23C;
}

.actual-payment {
  font-weight: bold;
  color: #67C23A;
}

.pagination-wrapper {
  margin-top: 20px;
  text-align: center;
}

.pending-row {
  background-color: #fdf6ec;
}

.completed-row {
  background-color: #f0f9ff;
}

/* 订单详情样式 */
.order-detail {
  max-height: 600px;
  overflow-y: auto;
}

.detail-section {
  margin-bottom: 30px;
}

.detail-section h4 {
  margin: 0 0 15px 0;
  color: #303133;
  font-size: 1.1rem;
  border-bottom: 2px solid #409EFF;
  padding-bottom: 5px;
}

.detail-item {
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}

.detail-item label {
  font-weight: 500;
  color: #606266;
  min-width: 80px;
  margin-right: 10px;
}

.detail-item .fee {
  font-weight: bold;
  color: #E6A23C;
}

.repair-record {
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  padding: 15px;
  margin-bottom: 15px;
}

.record-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.record-time {
  color: #909399;
  font-size: 0.9rem;
}

.record-content p {
  margin: 5px 0;
  line-height: 1.5;
}

.no-data {
  color: #909399;
  text-align: center;
  font-style: italic;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .customer-service-orders {
    padding: 10px;
  }
  
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .header-left h2 {
    font-size: 1.5rem;
  }
  
  .header-left p {
    font-size: 0.9rem;
  }
  
  .header-actions {
    width: 100%;
  }
  
  .header-actions .el-button {
    width: 100%;
  }
  
  .stats-cards {
    grid-template-columns: 1fr;
    gap: 15px;
    padding: 15px;
  }
  
  .stat-card {
    padding: 15px;
  }
  
  .stat-icon {
    width: 50px;
    height: 50px;
    font-size: 1.2rem;
    margin-right: 12px;
  }
  
  .stat-number {
    font-size: 1.5rem;
  }
  
  .stat-label {
    font-size: 0.8rem;
  }
  
  .orders-section {
    padding: 15px;
  }
  
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .section-header h3 {
    font-size: 1.1rem;
  }
  
  .problem-desc {
    max-width: 120px;
  }
  
  /* 移动端表格优化 */
  .el-table {
    font-size: 0.85rem;
  }
  
  .el-table th {
    padding: 8px 4px;
  }
  
  .el-table td {
    padding: 8px 4px;
  }
  
  /* 移动端按钮优化 */
  .el-button {
    padding: 8px 12px;
    font-size: 0.85rem;
  }
  
  .el-button--mini {
    padding: 6px 8px;
    font-size: 0.8rem;
  }
  
  /* 移动端分页优化 */
  .pagination-wrapper {
    margin-top: 15px;
  }
  
  .el-pagination {
    font-size: 0.85rem;
  }
  
  .el-pagination .el-pager li {
    min-width: 28px;
    height: 28px;
    line-height: 28px;
  }
  
  /* 移动端对话框优化 */
  .el-dialog {
    width: 95% !important;
    margin: 5vh auto !important;
  }
  
  .el-dialog__body {
    padding: 15px;
  }
  
  .order-detail {
    max-height: 70vh;
  }
  
  .detail-section {
    margin-bottom: 20px;
  }
  
  .detail-section h4 {
    font-size: 1rem;
  }
  
  .detail-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
  
  .detail-item label {
    min-width: auto;
    margin-right: 0;
  }
  
  .repair-record {
    padding: 12px;
    margin-bottom: 12px;
  }
  
  .record-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
}

/* 超小屏幕优化 */
@media (max-width: 480px) {
  .customer-service-orders {
    padding: 8px;
  }
  
  .header-content {
    gap: 12px;
  }
  
  .header-left h2 {
    font-size: 1.3rem;
  }
  
  .header-left p {
    font-size: 0.85rem;
  }
  
  .stats-cards {
    padding: 10px;
    gap: 10px;
  }
  
  .stat-card {
    padding: 12px;
  }
  
  .stat-icon {
    width: 45px;
    height: 45px;
    font-size: 1rem;
    margin-right: 10px;
  }
  
  .stat-number {
    font-size: 1.3rem;
  }
  
  .orders-section {
    padding: 10px;
  }
  
  .problem-desc {
    max-width: 100px;
  }
  
  .el-table {
    font-size: 0.8rem;
  }
  
  .el-table th,
  .el-table td {
    padding: 6px 3px;
  }
  
  .el-button {
    padding: 6px 10px;
    font-size: 0.8rem;
  }
  
  .el-button--mini {
    padding: 4px 6px;
    font-size: 0.75rem;
  }
  
  .el-dialog {
    width: 98% !important;
    margin: 2vh auto !important;
  }
  
  .el-dialog__body {
    padding: 10px;
  }
}
</style>
